/** Button primitive (shadcn/ui style, Radix Slot for `asChild`). */
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactElement } from "react";

import { cn } from "@/shared/lib/format";

/** Visual variants for `Button`. */
export const buttonVariants = cva(
  "inline-flex max-w-full items-center justify-center gap-1.5 whitespace-normal rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "border border-brand/50 bg-secondary font-semibold text-secondary-foreground hover:bg-accent",
        outline:
          "border border-input bg-card hover:border-brand hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        link: "text-brand-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "min-h-8 px-3 py-1.5",
        sm: "min-h-7 rounded-sm px-2.5 py-1 text-xs",
        lg: "min-h-9 rounded-md px-5 py-1.5",
        icon: "h-8 w-8 shrink-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

/** Props for `Button`. */
export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `<button>` (e.g. a router link). */
  asChild?: boolean;
}

/** Accessible button with consistent variants. */
export function Button({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  ...props
}: ButtonProps): ReactElement {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...(asChild ? {} : { type })}
      {...props}
    />
  );
}
